import type { ReactNode } from 'react';
import { FilterPill, Input, Item } from '@/components/ui';
import styles from './CoverageRail.module.css';

/**
 * EAA-1817: the shell both coverage rails share — heading, search box, the
 * bucket pills, the scrolling row list and the footer summary (frame 55's left
 * 300px column).
 *
 * WHY IT IS SHARED RATHER THAN COPIED. The **Theo ứng dụng** and **Theo vai
 * trò** views draw the same rail with a different population in it, and the two
 * frames-worth of chrome around that population is identical down to the pill
 * heights. Copying it would have given the screen two rails to keep in step —
 * the same failure `AccountsToolbar` exists to close for the three accounts
 * tables.
 *
 * WHY THE PILLS AND ROWS ARE KIT COMPONENTS NOW. This shell replaces
 * `AppCoverageRail`'s hand-written markup: three `<button className="rc-rail-tab">`
 * pills that styled their own pressed state, and one `<button className="rc-app-row">`
 * per row that re-derived a list row's padding, tile and selected tint. Both had
 * kit equivalents (`FilterPill`, added with this change from the same frames;
 * `Item`, which has been the sheet's list row since EAA-1804) and neither was
 * reaching for them — the exact drift EAA-1820/EAA-1826 exist to stop.
 */
export interface CoverageRailBucket<TTab extends string> {
    key: TTab;
    label: string;
    /** Omit for the "all" bucket — the design prints no number on it here. */
    count?: number;
}

export interface CoverageRailRow {
    key: string;
    /** Leading monospace code tile. */
    tile: ReactNode;
    title: ReactNode;
    /** The coverage bar — rendered under the title. */
    caption: ReactNode;
    /** Right-hand side: the defect badge and the `n/m` ratio. */
    trailing: ReactNode;
    /** Full sentence for screen readers; the visual row is several fragments. */
    ariaLabel: string;
}

export interface CoverageRailShellProps<TTab extends string> {
    title: string;
    ariaLabel: string;
    searchPlaceholder: string;
    searchAriaLabel: string;
    search: string;
    onSearchChange: (next: string) => void;
    bucketsAriaLabel: string;
    buckets: CoverageRailBucket<TTab>[];
    activeBucket: TTab;
    onBucketChange: (next: TTab) => void;
    rows: CoverageRailRow[];
    selectedKey: string | null;
    onSelect: (key: string) => void;
    noMatchText: string;
    footer: ReactNode;
}

export function CoverageRailShell<TTab extends string>({
    title,
    ariaLabel,
    searchPlaceholder,
    searchAriaLabel,
    search,
    onSearchChange,
    bucketsAriaLabel,
    buckets,
    activeBucket,
    onBucketChange,
    rows,
    selectedKey,
    onSelect,
    noMatchText,
    footer,
}: CoverageRailShellProps<TTab>) {
    return (
        <section className={styles.rail} aria-label={ariaLabel}>
            {/* Frame 55's rail starts at the search box — there is no printed
                heading above it, the panel's identity comes from the page title
                and the switcher. The heading is kept for assistive tech only:
                a landmark region still needs a name, and the count in it
                ("Ứng dụng tích hợp 8") is worth hearing. */}
            <h2 className="portal-sr-only">{title}</h2>

            <Input
                className="hz-search"
                placeholder={searchPlaceholder}
                value={search}
                onChange={(event) => onSearchChange(event.target.value)}
                aria-label={searchAriaLabel}
                prefix={
                    <svg
                        viewBox="0 0 24 24"
                        width="15"
                        height="15"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.8}
                        aria-hidden="true"
                    >
                        <circle cx="11" cy="11" r="7" />
                        <path d="m20 20-3.5-3.5" strokeLinecap="round" />
                    </svg>
                }
            />

            <div className={styles.railTabs} role="group" aria-label={bucketsAriaLabel}>
                {buckets.map((bucket) => (
                    <FilterPill
                        key={bucket.key}
                        size="sm"
                        selected={activeBucket === bucket.key}
                        count={bucket.count}
                        onClick={() => onBucketChange(bucket.key)}
                    >
                        {bucket.label}
                    </FilterPill>
                ))}
            </div>

            {rows.length === 0 ? (
                <p className="portal-admin-hint">{noMatchText}</p>
            ) : (
                <ul className={styles.railList}>
                    {rows.map((row) => (
                        <li key={row.key}>
                            {/* `showChevron={false}`: the frame's rows carry a
                                coverage bar and a ratio on the right, and a
                                chevron beside those reads as a third piece of
                                metadata rather than an affordance. */}
                            <Item
                                className={styles.appRow}
                                leading={row.tile}
                                title={row.title}
                                caption={row.caption}
                                trailing={row.trailing}
                                selected={row.key === selectedKey}
                                // Frame 55 draws the selected rail row with a
                                // 3px leading accent bar, not a ring round the
                                // whole row — see `Item`'s `selectedVariant`.
                                selectedVariant="edge"
                                showChevron={false}
                                onClick={() => onSelect(row.key)}
                                ariaLabel={row.ariaLabel}
                            />
                        </li>
                    ))}
                </ul>
            )}

            <div className={styles.railFoot}>{footer}</div>
        </section>
    );
}

export default CoverageRailShell;
